import { useEffect, useState } from 'react'
import { apiClient } from './api'

export type WorkspaceModeValue = 'simple' | 'pmo'
export interface WorkspaceMode {
  mode: WorkspaceModeValue
  can_change: boolean
}

// Tabs shown in simple mode. Everything else (RAID, gates, governance,
// budget, QA, comms plan…) appears once an admin turns on PMO tools.
export const SIMPLE_MODE_TABS = new Set(['dashboard', 'tasks', 'timeline', 'documents', 'reports', 'collab', 'sharing'])

let cache: WorkspaceMode | null = null
const listeners = new Set<(m: WorkspaceMode) => void>()

export const workspaceModeService = {
  async get(): Promise<WorkspaceMode> {
    const { data } = await apiClient.get<WorkspaceMode>('/api/v1/workspace-mode')
    cache = data
    return data
  },
  async set(mode: WorkspaceModeValue): Promise<WorkspaceMode> {
    const { data } = await apiClient.put<WorkspaceMode>('/api/v1/workspace-mode', { mode, can_change: true })
    cache = data
    listeners.forEach((l) => l(data))
    return data
  },
}

/** Current workspace mode. Defaults to 'pmo' (show everything) until loaded
 *  or if the request fails, so nobody loses access to a tab by accident. */
export function useWorkspaceMode(): [WorkspaceMode, (mode: WorkspaceModeValue) => Promise<void>] {
  const [state, setState] = useState<WorkspaceMode>(cache ?? { mode: 'pmo', can_change: false })
  useEffect(() => {
    listeners.add(setState)
    if (!cache) workspaceModeService.get().then(setState).catch(() => undefined)
    return () => {
      listeners.delete(setState)
    }
  }, [])
  const change = async (mode: WorkspaceModeValue) => {
    await workspaceModeService.set(mode)
  }
  return [state, change]
}
